import { useMemo, useState } from 'react';
import { post } from '../api';
import { useChat } from '../state';
import { IconLink, IconPlus, IconSearch, IconSettings } from './icons';
import { Avatar, PLATFORM_LABEL, formatListTime } from './ui';

interface SidebarProps {
  onNewRoom: () => void;
  onJoin: () => void;
}

export function Sidebar({ onNewRoom, onJoin }: SidebarProps) {
  const conversations = useChat((state) => state.conversations);
  const activeId = useChat((state) => state.activeId);
  const connected = useChat((state) => state.connected);
  const capabilities = useChat((state) => state.capabilities);
  const setActive = useChat((state) => state.setActive);
  const markRead = useChat((state) => state.markRead);
  const toggleSettings = useChat((state) => state.toggleSettings);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return conversations;
    return conversations.filter(
      (item) =>
        item.title.toLowerCase().includes(needle) ||
        (item.preview ?? '').toLowerCase().includes(needle) ||
        PLATFORM_LABEL[item.platform].toLowerCase().includes(needle),
    );
  }, [conversations, query]);

  const open = (id: string) => {
    setActive(id);
    markRead(id);
    void post(`/api/conversations/${encodeURIComponent(id)}/read`).catch(() => {});
  };

  return (
    <aside className="sidebar">
      <header className="sidebar-head">
        <div className="brand">
          <div className="brand-mark">C</div>
          <div>
            <strong>Chatsi</strong>
            <span className={`conn ${connected ? 'on' : 'off'}`}>{connected ? 'live' : 'offline'}</span>
          </div>
        </div>
        <button className="icon-btn" onClick={() => toggleSettings(true)} title="Settings" aria-label="Settings">
          <IconSettings />
        </button>
      </header>

      <div className="search">
        <IconSearch />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search chats"
          aria-label="Search chats"
        />
      </div>

      <div className="quick">
        <button className="quick-btn" onClick={onNewRoom}>
          <IconPlus size={16} /> Mesh room
        </button>
        <button className="quick-btn" onClick={onJoin}>
          <IconLink size={16} /> Join
        </button>
      </div>

      <div className="list-head">
        <span>Chats</span>
        <span className="list-count">{filtered.length}</span>
      </div>

      <ul className="conv-list">
        {filtered.map((conversation) => (
          <li key={conversation.id}>
            <button
              className={`conv ${activeId === conversation.id ? 'active' : ''}`}
              onClick={() => open(conversation.id)}
            >
              <Avatar name={conversation.title} url={conversation.avatar} platform={conversation.platform} />
              <div className="conv-body">
                <div className="conv-row">
                  <span className="conv-title">{conversation.title}</span>
                  <span className="conv-time">{formatListTime(conversation.lastMessageAt)}</span>
                </div>
                <div className="conv-row">
                  <span className="conv-preview">{conversation.preview ?? PLATFORM_LABEL[conversation.platform]}</span>
                  {conversation.unread > 0 ? <span className="badge">{conversation.unread > 99 ? '99+' : conversation.unread}</span> : null}
                </div>
              </div>
            </button>
          </li>
        ))}
        {!filtered.length ? <li className="empty-list">No chats yet. Connect Telegram or Discord, or create a mesh room.</li> : null}
      </ul>

      <footer className="sidebar-foot">
        <span className={`pill ${capabilities?.telegram.enabled ? 'ok' : ''}`}>TG</span>
        <span className={`pill ${capabilities?.discord.enabled ? 'ok' : ''}`}>DC</span>
        <span className={`pill ${capabilities?.mesh.enabled ? 'ok' : ''}`}>P2P {capabilities?.mesh.peers ?? 0}</span>
        <span className={`pill ${capabilities?.livekit.enabled ? 'ok' : ''}`}>Calls</span>
        <span className={`pill ${capabilities?.ai.enabled ? 'ok' : ''}`}>AI</span>
      </footer>
    </aside>
  );
}
